import React, { useReducer, useCallback, useMemo, useRef, useState } from "react";
import type { ReactElement } from "react";

import { Modal, VStack, Text, HStack, Checkbox, ScrollView, Button, useToast } from "native-base";
import { useMutation } from "react-relay";

import ButtonDebounced from "pianofunclub-shared/components/Buttons/ButtonDebounced";
import MultiSelect from "pianofunclub-shared/components/Filters/MultiSelect";
import TextInput from "pianofunclub-shared/components/Inputs/TextInput";
import type { TextInputRef } from "pianofunclub-shared/components/Inputs/TextInput";
import SchoolMultiSelectModal from "pianofunclub-shared/components/Modals/SchoolMultiSelectModal";
import TeacherMultiSelectModal from "pianofunclub-shared/components/Modals/TeacherMultiSelectModal";
import Select from "pianofunclub-shared/components/NativeBaseExtended/Select";
import ToastAlert from "pianofunclub-shared/components/NativeBaseExtended/ToastAlert";
import { ChevronLeftIcon } from "pianofunclub-shared/components/Other/Icons";
import { useAuth } from "pianofunclub-shared/providers/AuthProviderContext";
import type {
  SendBulkCommunicationMutation,
  SendBulkCommunicationMutation$data,
} from "pianofunclub-shared/relay/graphql/accounts/__generated__/SendBulkCommunicationMutation.graphql";
import { send_bulk_communication } from "pianofunclub-shared/relay/graphql/accounts/SendBulkCommunication";
import {
  BLOCKS,
  INSTRUMENTS_FOR_SELECT,
  SCHOOL_YEARS,
  TEACHING_DAYS,
} from "pianofunclub-shared/utils/constants";
import {
  listToStringWithCommasConverter,
  listWithAndConverter,
  listWithOrConverter,
  messagePreviewConverter,
  schoolYearConverter,
  sendTypeConverter,
} from "pianofunclub-shared/utils/converters";
import {
  getCurrentBlock,
  getDefaultStartingYearAndStartingYearsForSelect,
  getErroredBulkCommunicationLabel,
} from "pianofunclub-shared/utils/extractors";
import { createReducer } from "pianofunclub-shared/utils/reducers";

interface Props {
  hideModal: () => void;
  onSendFinish?: (success: boolean) => void;
  onSendStart?: () => void;
  showModal: boolean;
}

type ReducerValues = {
  block: number;
  excludePupilsWhoHaveBook: boolean;
  excludePupilsWhoHaveInstrument: boolean;
  excludePupilsWhoHaveInstrumentSuppliedByUs: boolean;
  excludePupilsWhoHaveReturnedInstrument: boolean;
  hasSelectedAllInstruments: boolean;
  hasSelectedAllLessonDays: boolean;
  hasSelectedAllSchools: boolean;
  hasSelectedAllSchoolYears: boolean;
  hasSelectedAllTeachers: boolean;
  messageString: string;
  messageSubject: string;
  selectedInstruments: string[];
  selectedLessonDays: string[];
  selectedSchoolIds: string[];
  selectedSchoolNames: string[];
  selectedSchoolYears: string[];
  selectedTeacherIds: string[];
  selectedTeacherNames: string[];
  sendType: string;
  startingYear: number;
};

const MESSAGES_TO_SEND_IN_BULK_COMMUNICATION_BATCH = 20;

const SendBulkCommunicationModal = (props: Props): ReactElement | null => {
  const { hideModal, onSendFinish, onSendStart, showModal } = props;

  const { user } = useAuth();

  const [defaultStartingYear, startingYears] = useMemo(
    () => getDefaultStartingYearAndStartingYearsForSelect(),
    []
  );

  const initialState = useMemo(() => {
    const initialStartingYear =
      user?.profile?.profileGroup?.currentStartingYear ?? defaultStartingYear;

    let initialBlock: number;

    if (
      user?.profile?.profileGroup?.currentStartingYear &&
      user?.profile?.profileGroup?.currentStartingYear > defaultStartingYear
    ) {
      initialBlock = 1;
    } else {
      initialBlock = user?.profile?.currentBlock ?? getCurrentBlock();
    }

    return {
      values: {
        block: Number(initialBlock),
        excludePupilsWhoHaveBook: false,
        excludePupilsWhoHaveInstrument: false,
        excludePupilsWhoHaveInstrumentSuppliedByUs: false,
        excludePupilsWhoHaveReturnedInstrument: false,
        hasSelectedAllInstruments: true,
        hasSelectedAllLessonDays: true,
        hasSelectedAllSchools: true,
        hasSelectedAllSchoolYears: true,
        hasSelectedAllTeachers: true,
        messageString: "",
        messageSubject: "",
        selectedInstruments: INSTRUMENTS_FOR_SELECT.map((item) => item.value),
        selectedLessonDays: TEACHING_DAYS.map((item) => item.value),
        selectedSchoolIds: [],
        selectedSchoolNames: [],
        selectedSchoolYears: SCHOOL_YEARS.map((item) => item.value),
        selectedTeacherIds: [],
        selectedTeacherNames: [],
        sendType: "EMAIL_SMS",
        startingYear: Number(initialStartingYear),
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reducer = createReducer<ReducerValues>(initialState);
  const [state, dispatchState] = useReducer(reducer, initialState);

  const [currentPageIndex, setCurrentPageIndex] = useState(0);

  const messageSubjectInputRef = useRef<TextInputRef>(null);
  const messageStringInputRef = useRef<TextInputRef>(null);

  const toast = useToast();

  const [commitSendBulkCommunication] =
    useMutation<SendBulkCommunicationMutation>(send_bulk_communication);

  const sendBulkCommunication = useCallback(
    (variables: {
      block: number;
      excludePupilsWhoHaveBook: boolean;
      excludePupilsWhoHaveInstrument: boolean;
      excludePupilsWhoHaveInstrumentSuppliedByUs: boolean;
      excludePupilsWhoHaveReturnedInstrument: boolean;
      instruments?: string[];
      lessonDays?: number[];
      messageString: string;
      messageSubject: string;
      schoolIds: string[];
      schoolYears: number[];
      sendType: string;
      startingYear: number;
      teacherIds?: string[];
    }) => {
      let hasErrored = false;
      const failedMutationItems: string[] = [];

      const sendBulkCommunicationConfig = (
        numberSent: number | undefined,
        totalToSend: number | undefined
      ) => {
        return {
          variables: {
            input: {
              ...variables,
              numberInBatch: MESSAGES_TO_SEND_IN_BULK_COMMUNICATION_BATCH,
              numberSent: numberSent,
              totalToSend: totalToSend,
            },
          },
          onCompleted: (response: SendBulkCommunicationMutation$data) => {
            if (response.sendBulkCommunication?.erroredProfile) {
              failedMutationItems.push(
                getErroredBulkCommunicationLabel(
                  response.sendBulkCommunication.erroredProfile?.user.firstName,
                  response.sendBulkCommunication.erroredProfile?.user.lastName,
                  response.sendBulkCommunication.erroredProfile?.user.email
                )
              );

              toast.show({
                // never auto-dismiss
                duration: null,
                render: ({ id }: { id: string }) => (
                  <ToastAlert
                    description={failedMutationItems.join("\n")}
                    id={id}
                    status="error"
                    title={"Failed to send to:"}
                    toast={toast}
                  />
                ),
              });
            }
            if (response?.sendBulkCommunication?.success) {
              if (
                response.sendBulkCommunication.numberSent !==
                  response.sendBulkCommunication.totalToSend &&
                response.sendBulkCommunication.profile?.profileGroup
                  ?.sendBulkCommunicationProgress != 100 &&
                !hasErrored
              ) {
                numberSent = response.sendBulkCommunication.numberSent ?? undefined;
                totalToSend = response.sendBulkCommunication.totalToSend ?? undefined;
                commitSendBulkCommunication(sendBulkCommunicationConfig(numberSent, totalToSend));
              } else {
                onSendFinish?.(true);
              }
            } else {
              hasErrored = true;
              toast.show({
                // never auto-dismiss
                duration: null,
                render: ({ id }: { id: string }) => (
                  <ToastAlert
                    description={"Please get in touch with one of the team"}
                    id={id}
                    status="error"
                    title={"Error sending bulk communication"}
                    toast={toast}
                  />
                ),
              });
              onSendFinish?.(false);
            }
          },
          // this happens if the API gateway times out for a batch
          // in this case, just fire off another mutation and continue
          onError: () => {
            // wait for 30 seconds to try and let the previous batch complete
            setTimeout(() => {
              if (
                numberSent !== undefined &&
                totalToSend !== undefined &&
                numberSent + MESSAGES_TO_SEND_IN_BULK_COMMUNICATION_BATCH < totalToSend
              ) {
                commitSendBulkCommunication(
                  sendBulkCommunicationConfig(
                    numberSent + MESSAGES_TO_SEND_IN_BULK_COMMUNICATION_BATCH,
                    totalToSend
                  )
                );
              } else {
                onSendFinish?.(false);
              }
            }, 30000);
          },
        };
      };

      // to avoid hitting the max AWS API Gateway execution time (29s)
      // split up the reminders to be sent into batches and recursively
      // fire the mutation, keeping a track of overall progress
      const numberOfSent: number | undefined = 0;
      const totalToSend: number | undefined = undefined;

      commitSendBulkCommunication(sendBulkCommunicationConfig(numberOfSent, totalToSend));
      onSendStart?.();
    },
    [commitSendBulkCommunication, onSendFinish, onSendStart, toast]
  );

  const messageFinishEditingHandler = useCallback(
    (inputIdentifier?: string | number, inputValue?: string | undefined) => {
      if (typeof inputValue === "string" && typeof inputIdentifier === "string") {
        dispatchState({
          input: inputIdentifier as keyof ReducerValues,
          value: inputValue,
        });
      }
    },
    []
  );

  const renderSelectSchools = useMemo(() => {
    return (
      <SchoolMultiSelectModal
        buttonText="Continue"
        description={
          "Filter for parents with children at certain schools.\nSelect all to not filter by school."
        }
        doNotHideOnSave
        hideCountFromButton
        hideModal={() => {
          setCurrentPageIndex(0);
          hideModal();
        }}
        initiallySelectAllSchools
        initiallySelectedSchoolIds={state.values.selectedSchoolIds}
        modalBodyProps={{
          _scrollview: {
            contentContainerStyle: {
              flexGrow: 1,
              justifyContent: "center",
            },
          },
        }}
        modalContentProps={{
          height: "660px",
          maxWidth: "800px",
          width: "800px",
        }}
        onSave={({ hasSelectedAllSchools, schoolIds, schoolNames }) => {
          dispatchState({
            input: "selectedSchoolIds",
            value: schoolIds,
          });
          dispatchState({
            input: "selectedSchoolNames",
            value: schoolNames,
          });
          dispatchState({
            input: "hasSelectedAllSchools",
            value: hasSelectedAllSchools,
          });
          setCurrentPageIndex((currentPageIndex) => currentPageIndex + 1);
        }}
        showModal={showModal && currentPageIndex === 0}
        title="Step 1: Filter by School"
        width={600}
      />
    );
  }, [currentPageIndex, hideModal, showModal, state.values.selectedSchoolIds]);

  const renderSelectTeachers = useMemo(() => {
    return (
      <TeacherMultiSelectModal
        buttonText="Continue"
        description={
          "Filter for parents with children taught by certain teachers.\nSelect all to not filter by teacher."
        }
        doNotHideOnSave
        hideCountFromButton
        hideModal={() => {
          setCurrentPageIndex(0);
          hideModal();
        }}
        includeBackButton
        initiallySelectAllTeachers
        initiallySelectedTeacherIds={state.values.selectedTeacherIds}
        modalBodyProps={{
          _scrollview: {
            contentContainerStyle: {
              flexGrow: 1,
              justifyContent: "center",
            },
          },
        }}
        modalContentProps={{
          height: "660px",
          maxWidth: "800px",
          width: "800px",
        }}
        onBackButtonPress={() => setCurrentPageIndex((currentPageIndex) => currentPageIndex - 1)}
        onSave={({ hasSelectedAllTeachers, teacherIds, teacherNames }) => {
          dispatchState({
            input: "selectedTeacherIds",
            value: teacherIds,
          });
          dispatchState({
            input: "selectedTeacherNames",
            value: teacherNames,
          });
          dispatchState({
            input: "hasSelectedAllTeachers",
            value: hasSelectedAllTeachers,
          });
          setCurrentPageIndex((currentPageIndex) => currentPageIndex + 1);
        }}
        showModal={showModal && currentPageIndex === 1}
        title="Step 2: Filter by Teacher"
        width={600}
      />
    );
  }, [showModal, currentPageIndex, state.values.selectedTeacherIds, hideModal]);

  const renderSelectYearGroups = useMemo(() => {
    return (
      <MultiSelect
        data={SCHOOL_YEARS}
        finishButtonText="Continue"
        onPressFinishButton={() => {
          dispatchState({
            input: "hasSelectedAllSchoolYears",
            value: state.values.selectedSchoolYears.length === SCHOOL_YEARS.length,
          });
          setCurrentPageIndex((currentPageIndex) => currentPageIndex + 1);
        }}
        selectedValues={state.values.selectedSchoolYears}
        setSelectedValues={(selectedValues) =>
          dispatchState({
            input: "selectedSchoolYears",
            value: selectedValues,
          })
        }
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.values.selectedSchoolYears, state.values.selectedSchoolYears.length]);

  const renderSelectInstruments = useMemo(() => {
    return (
      <MultiSelect
        data={INSTRUMENTS_FOR_SELECT}
        finishButtonText="Continue"
        onPressFinishButton={() => {
          dispatchState({
            input: "hasSelectedAllInstruments",
            value: state.values.selectedInstruments.length === INSTRUMENTS_FOR_SELECT.length,
          });
          setCurrentPageIndex((currentPageIndex) => currentPageIndex + 1);
        }}
        selectedValues={state.values.selectedInstruments}
        setSelectedValues={(selectedValues) =>
          dispatchState({
            input: "selectedInstruments",
            value: selectedValues,
          })
        }
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.values.selectedInstruments, state.values.selectedInstruments.length]);

  const renderSelectLessonDays = useMemo(() => {
    return (
      <MultiSelect
        data={TEACHING_DAYS}
        finishButtonText="Continue"
        onPressFinishButton={() => {
          dispatchState({
            input: "hasSelectedAllLessonDays",
            value: state.values.selectedLessonDays.length === TEACHING_DAYS.length,
          });
          setCurrentPageIndex((currentPageIndex) => currentPageIndex + 1);
        }}
        selectedValues={state.values.selectedLessonDays}
        setSelectedValues={(selectedValues) =>
          dispatchState({
            input: "selectedLessonDays",
            value: selectedValues,
          })
        }
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.values.selectedLessonDays, state.values.selectedLessonDays.length]);

  const renderOtherOptions = useMemo(() => {
    return (
      <>
        <VStack alignItems="center" flex={1} space="8" width="85%">
          <VStack alignItems="center" space="4">
            <Text fontSize="lg">Pupils with lessons in</Text>
            <HStack space="3">
              <Select
                bg="surface.100"
                borderRadius="2xl"
                color="surface.900"
                fontSize="md"
                onValueChange={(itemValue) => {
                  const value = parseInt(itemValue);

                  if (value > state.values.startingYear) {
                    dispatchState({
                      input: "block",
                      value: 1,
                    });
                  }

                  dispatchState({
                    input: "startingYear",
                    value: value,
                  });
                }}
                placeholder="Select year"
                py="2.5"
                selectedValue={String(state.values.startingYear)}
                width="40"
              >
                {startingYears.map((item) => {
                  return (
                    <Select.Item
                      key={item.value}
                      actionSheetLabel={item.label}
                      value={String(item.value)}
                    />
                  );
                })}
              </Select>
              <Select
                bg="surface.100"
                borderRadius="2xl"
                color="surface.900"
                fontSize="md"
                onValueChange={(itemValue) => {
                  dispatchState({
                    input: "block",
                    value: parseInt(itemValue),
                  });
                }}
                placeholder="Select block"
                py="2.5"
                selectedValue={String(state.values.block)}
                width="40"
              >
                {BLOCKS.map((item) => {
                  return (
                    <Select.Item
                      key={item.value}
                      actionSheetLabel={item.label}
                      value={item.value}
                    />
                  );
                })}
              </Select>
            </HStack>
          </VStack>
          <VStack alignItems="center" space="4">
            <Text fontSize="lg">Exclude pupils who</Text>
            <HStack space="3">
              <Text color="surface.700" fontSize="md">
                Have a book?
              </Text>
              <Checkbox
                defaultIsChecked={state.values.excludePupilsWhoHaveBook}
                onChange={(isSelected) =>
                  dispatchState({
                    input: "excludePupilsWhoHaveBook",
                    value: isSelected,
                  })
                }
                size="md"
                value="HAVE_BOOK"
              />
            </HStack>
            <HStack space="3">
              <Text color="surface.700" fontSize="md">
                Have an instrument?
              </Text>
              <Checkbox
                defaultIsChecked={state.values.excludePupilsWhoHaveInstrument}
                onChange={(isSelected) =>
                  dispatchState({
                    input: "excludePupilsWhoHaveInstrument",
                    value: isSelected,
                  })
                }
                size="md"
                value="HAVE_INSTRUMENT"
              />
            </HStack>
            <HStack space="3">
              <Text color="surface.700" fontSize="md">
                Have an instrument supplied by us?
              </Text>
              <Checkbox
                defaultIsChecked={state.values.excludePupilsWhoHaveInstrumentSuppliedByUs}
                onChange={(isSelected) =>
                  dispatchState({
                    input: "excludePupilsWhoHaveInstrumentSuppliedByUs",
                    value: isSelected,
                  })
                }
                size="md"
                value="HAVE_INSTRUMENT_SUPPLIED_BY_US"
              />
            </HStack>
            <HStack mb="2" space="3">
              <Text color="surface.700" fontSize="md">
                Have returned their instrument?
              </Text>
              <Checkbox
                defaultIsChecked={state.values.excludePupilsWhoHaveReturnedInstrument}
                onChange={(isSelected) =>
                  dispatchState({
                    input: "excludePupilsWhoHaveReturnedInstrument",
                    value: isSelected,
                  })
                }
                size="md"
                value="HAVE_RETURNED_INSTRUMENT"
              />
            </HStack>
          </VStack>
          <VStack alignItems="center" space="4">
            <Text fontSize="lg">Send by</Text>
            <Select
              bg="surface.100"
              color="surface.900"
              fontSize="md"
              onValueChange={(itemValue) =>
                dispatchState({
                  input: "sendType",
                  value: itemValue,
                })
              }
              selectedValue={state.values.sendType}
              textAlign="center"
              width="250px"
            >
              <Select.Item
                key={"EMAIL_SMS"}
                actionSheetLabel={"Email with SMS fallback"}
                value={"EMAIL_SMS"}
              />
              <Select.Item key={"EMAIL"} actionSheetLabel={"Email only"} value={"EMAIL"} />
              <Select.Item
                key={"SMS_EMAIL"}
                actionSheetLabel={"SMS with email fallback"}
                value={"SMS_EMAIL"}
              />
              <Select.Item key={"SMS"} actionSheetLabel={"SMS only"} value={"SMS"} />
            </Select>
          </VStack>
        </VStack>
        <ButtonDebounced
          _text={{ fontSize: "xl" }}
          height="56px"
          mb="2"
          mt="30px"
          onPress={() => setCurrentPageIndex((currentPageIndex) => currentPageIndex + 1)}
          px="6"
        >
          Continue
        </ButtonDebounced>
      </>
    );
  }, [
    startingYears,
    state.values.block,
    state.values.excludePupilsWhoHaveBook,
    state.values.excludePupilsWhoHaveInstrument,
    state.values.excludePupilsWhoHaveInstrumentSuppliedByUs,
    state.values.excludePupilsWhoHaveReturnedInstrument,
    state.values.sendType,
    state.values.startingYear,
  ]);

  const renderMessageInput = useMemo(() => {
    return (
      <>
        <VStack alignItems="center" flex={1} space="6" width="95%">
          <Text color="surface.600" mx="6" textAlign="center">
            {`You can use {{firstName}}, {{fullName}} and {{accountNumber}} to personalise the message${
              state.values.sendType !== "SMS" ? " and/or subject" : ""
            }.`}
          </Text>
          {state.values.sendType !== "SMS" ? (
            <TextInput
              ref={messageSubjectInputRef}
              _focus={{ bg: "white" }}
              _hover={{ bg: "white" }}
              autoCapitalize="none"
              bg="white"
              borderRadius="xl"
              fontFamily="Poppins-Regular"
              id="messageSubject"
              initiallyValid
              initialValue={state.values.messageSubject}
              keyboardType="default"
              label={`Subject${state.values.sendType === "SMS_EMAIL" ? " (for fallback)" : ""}`}
              labelStyle={{
                color: "surface.700",
                fontSize: "sm",
              }}
              onFinishEditing={messageFinishEditingHandler}
              onSubmit={() => messageStringInputRef.current?.focus()}
              py="1.5"
              size="lg"
            />
          ) : null}
          <TextInput
            ref={messageStringInputRef}
            _focus={{ bg: "white" }}
            _hover={{ bg: "white" }}
            autoCapitalize="sentences"
            bg="white"
            borderRadius="xl"
            fontFamily="Poppins-Regular"
            height="200px"
            id="messageString"
            initialValue={state.values.messageString}
            keyboardType="default"
            label="Message"
            labelStyle={{
              color: "surface.700",
              fontSize: "sm",
            }}
            multiline
            onFinishEditing={messageFinishEditingHandler}
            size="lg"
          />
        </VStack>
        <ButtonDebounced
          _text={{ fontSize: "xl" }}
          height="56px"
          isDisabled={
            !state.values.messageString ||
            (!state.values.messageSubject && state.values.sendType !== "SMS")
          }
          mb="4"
          mt="10"
          onPress={() => setCurrentPageIndex((currentPageIndex) => (currentPageIndex += 1))}
          px="6"
        >
          Preview
        </ButtonDebounced>
      </>
    );
  }, [
    messageFinishEditingHandler,
    state.values.messageString,
    state.values.messageSubject,
    state.values.sendType,
  ]);

  const renderMessagePreview = useMemo(() => {
    const selectedSchoolsText = !state.values.hasSelectedAllSchools
      ? `- At ${listWithOrConverter(
          listToStringWithCommasConverter(state.values.selectedSchoolNames)
        )}`
      : null;
    const selectedTeachersText = !state.values.hasSelectedAllTeachers
      ? `- Taught by ${listWithOrConverter(
          listToStringWithCommasConverter(state.values.selectedTeacherNames)
        )}`
      : null;
    const selectedSchoolYearsText = !state.values.hasSelectedAllSchoolYears
      ? `- In ${listWithOrConverter(
          listToStringWithCommasConverter(
            state.values.selectedSchoolYears
              .sort((a, b) => parseInt(a) - parseInt(b))
              .map((item) => schoolYearConverter(parseInt(item)))
          )
        )}`
      : null;
    const selectedInstrumentsText = !state.values.hasSelectedAllInstruments
      ? `- Learning ${listWithOrConverter(
          listToStringWithCommasConverter(state.values.selectedInstruments)
        )}`
      : null;
    const selectedLessonDaysText = !state.values.hasSelectedAllLessonDays
      ? `- With lessons on ${listWithOrConverter(
          listToStringWithCommasConverter(
            state.values.selectedLessonDays
              .sort((a, b) => parseInt(a) - parseInt(b))
              .map(
                (item) =>
                  TEACHING_DAYS.find((teachingDay) => teachingDay.value === item)?.label ?? ""
              )
          )
        )}`
      : null;
    const otherFiltersText =
      state.values.excludePupilsWhoHaveBook ||
      state.values.excludePupilsWhoHaveInstrument ||
      state.values.excludePupilsWhoHaveInstrumentSuppliedByUs ||
      state.values.excludePupilsWhoHaveReturnedInstrument
        ? `- Excluding those who ${listWithAndConverter(
            listToStringWithCommasConverter(
              [
                state.values.excludePupilsWhoHaveBook && "have a book",
                state.values.excludePupilsWhoHaveInstrument && "have an instrument",
                state.values.excludePupilsWhoHaveInstrumentSuppliedByUs &&
                  "have an instrument supplied by us",
                state.values.excludePupilsWhoHaveReturnedInstrument &&
                  "have returned their instrument",
              ].filter((item) => item !== false)
            )
          )}`
        : null;

    const noFiltersApplied =
      !selectedSchoolsText &&
      !selectedTeachersText &&
      !selectedSchoolYearsText &&
      !selectedInstrumentsText &&
      !selectedLessonDaysText &&
      !otherFiltersText;

    return (
      <>
        <VStack alignItems="center" flex={1} space="6" width="95%">
          {state.values.sendType !== "SMS" ? (
            <VStack space="1" width="100%">
              <Text color="surface.700">Subject</Text>
              <Text
                borderColor="surface.200"
                borderRadius="xl"
                borderWidth={1}
                fontFamily="Poppins-Regular"
                fontSize="md"
                mx="-1"
                px="2"
                py="1.5"
              >
                {messagePreviewConverter(
                  state.values.messageSubject,
                  state.values.selectedTeacherNames[0]
                )}
              </Text>
            </VStack>
          ) : null}
          <VStack space="1" width="100%">
            <Text color="surface.700">Message</Text>
            <ScrollView
              borderColor="surface.200"
              borderRadius="xl"
              borderWidth={1}
              height="150px"
              mx="-1"
              px="2"
              py="1.5"
            >
              <Text fontFamily="Poppins-Regular" fontSize="md">
                {messagePreviewConverter(
                  state.values.messageString,
                  state.values.selectedTeacherNames[0]
                )}
              </Text>
            </ScrollView>
          </VStack>
          <VStack width="100%">
            <Text
              color="surface.700"
              fontFamily="Poppins-Regular"
              fontSize="md"
            >{`Sending by ${sendTypeConverter(
              state.values.sendType
            )} to ${noFiltersApplied ? "all active parents" : "active parents with children:"}`}</Text>
            {selectedSchoolsText ? (
              <Text color="surface.700" fontFamily="Poppins-Regular" fontSize="md">
                {selectedSchoolsText}
              </Text>
            ) : null}
            {selectedTeachersText ? (
              <Text color="surface.700" fontFamily="Poppins-Regular" fontSize="md">
                {selectedTeachersText}
              </Text>
            ) : null}
            {selectedSchoolYearsText ? (
              <Text color="surface.700" fontFamily="Poppins-Regular" fontSize="md">
                {selectedSchoolYearsText}
              </Text>
            ) : null}
            {selectedInstrumentsText ? (
              <Text color="surface.700" fontFamily="Poppins-Regular" fontSize="md">
                {selectedInstrumentsText}
              </Text>
            ) : null}
            {selectedLessonDaysText ? (
              <Text color="surface.700" fontFamily="Poppins-Regular" fontSize="md">
                {selectedLessonDaysText}
              </Text>
            ) : null}
            {otherFiltersText ? (
              <Text color="surface.700" fontFamily="Poppins-Regular" fontSize="md">
                {otherFiltersText}
              </Text>
            ) : null}
          </VStack>
        </VStack>
        <ButtonDebounced
          _text={{ fontSize: "xl" }}
          height="56px"
          mb="4"
          mt="8"
          onPress={() => {
            sendBulkCommunication({
              block: state.values.block,
              excludePupilsWhoHaveBook: state.values.excludePupilsWhoHaveBook,
              excludePupilsWhoHaveInstrument: state.values.excludePupilsWhoHaveInstrument,
              excludePupilsWhoHaveInstrumentSuppliedByUs:
                state.values.excludePupilsWhoHaveInstrumentSuppliedByUs,
              excludePupilsWhoHaveReturnedInstrument:
                state.values.excludePupilsWhoHaveReturnedInstrument,
              instruments: state.values.selectedInstruments.map((item) => item.toLowerCase()),
              lessonDays: state.values.selectedLessonDays.map((item) => parseInt(item)),
              messageString: state.values.messageString,
              messageSubject: state.values.messageSubject,
              schoolIds: state.values.selectedSchoolIds,
              schoolYears: state.values.selectedSchoolYears.map((item) => parseInt(item)),
              sendType: state.values.sendType,
              startingYear: state.values.startingYear,
              teacherIds: !state.values.hasSelectedAllTeachers
                ? state.values.selectedTeacherIds
                : undefined,
            });
            hideModal();
          }}
          px="6"
        >
          Send
        </ButtonDebounced>
      </>
    );
  }, [
    state.values.hasSelectedAllSchools,
    state.values.selectedSchoolNames,
    state.values.hasSelectedAllTeachers,
    state.values.selectedTeacherNames,
    state.values.hasSelectedAllSchoolYears,
    state.values.selectedSchoolYears,
    state.values.hasSelectedAllInstruments,
    state.values.selectedInstruments,
    state.values.hasSelectedAllLessonDays,
    state.values.selectedLessonDays,
    state.values.excludePupilsWhoHaveBook,
    state.values.excludePupilsWhoHaveInstrument,
    state.values.excludePupilsWhoHaveInstrumentSuppliedByUs,
    state.values.excludePupilsWhoHaveReturnedInstrument,
    state.values.sendType,
    state.values.messageSubject,
    state.values.messageString,
    state.values.selectedSchoolIds,
    state.values.selectedTeacherIds,
    state.values.startingYear,
    state.values.block,
    sendBulkCommunication,
    hideModal,
  ]);

  let modalContent: ReactElement;
  let modalHeader: string;
  let modalDescription: string | undefined = undefined;

  switch (currentPageIndex) {
    case 7:
      modalContent = renderMessagePreview;
      modalHeader = "Step 8: Preview of Bulk Email/SMS";
      break;
    case 6:
      modalContent = renderMessageInput;
      modalHeader = "Step 7: Message for Bulk Email/SMS";
      break;
    case 5:
      modalContent = renderOtherOptions;
      modalHeader = "Step 6: Time Period, Other Filters & Send Type";
      break;
    case 4:
      modalContent = renderSelectLessonDays;
      modalHeader = "Step 5: Filter by Lesson Day";
      modalDescription =
        "Filter for parents with children with lessons on certain days.\nSelect all to not filter by lesson day.";
      break;
    case 3:
      modalContent = renderSelectInstruments;
      modalHeader = "Step 4: Filter by Instrument";
      modalDescription =
        "Filter for parents with children learning certain instruments.\nSelect all to not filter by instrument.";
      break;
    default:
      modalContent = renderSelectYearGroups;
      modalHeader = "Step 3: Filter by Year Group";
      modalDescription =
        "Filter for parents with children in certain year groups.\nSelect all to not filter by year group.";
      break;
  }

  return (
    <>
      {renderSelectSchools}
      {renderSelectTeachers}
      <Modal
        closeOnOverlayClick={false}
        isOpen={showModal && currentPageIndex > 1}
        onClose={hideModal}
        size="xl"
      >
        <Modal.Content height="660px" maxWidth="800px" width="800px">
          <Button
            _hover={{ bg: "transparent", opacity: 0.8 }}
            _pressed={{ bg: "transparent", opacity: 0.7 }}
            bg="transparent"
            left="3"
            leftIcon={<ChevronLeftIcon ml="0" p="0" size="5" />}
            onPress={() => {
              setCurrentPageIndex((currentPageIndex) => currentPageIndex - 1);
            }}
            p="1"
            position="absolute"
            top="4"
            variant="ghost"
            zIndex="1"
          />
          <Modal.CloseButton
            _hover={{ bg: "transparent", opacity: 0.7 }}
            _pressed={{ bg: "transparent", opacity: 0.7 }}
          />
          <Modal.Header>{modalHeader}</Modal.Header>
          {modalDescription ? (
            <Text alignSelf="center" fontFamily="Poppins-Regular" fontSize="md" textAlign="center">
              {modalDescription}
            </Text>
          ) : null}
          <Modal.Body
            _scrollview={{
              contentContainerStyle: {
                flexGrow: 1,
                justifyContent: "center",
              },
            }}
            alignItems="center"
            mb="4"
            px="6"
          >
            {modalContent}
          </Modal.Body>
        </Modal.Content>
      </Modal>
    </>
  );
};

export default React.memo(SendBulkCommunicationModal);
